import { useEffect, useState } from 'react';
import SliderInputField from './Fields/sliderWithInput';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	ImageActionDiv, ImageEditorDiv, ImageEditornputField,
	ImageInfoDiv, ImageNameDiv, CustomImageHeading,
	ImageinfoWrap
} from './style';
import { toggleImageEditing, toggleMapVisibility, editMap, removeCustomMap, setBaselineMap } from 'store/slices/CustomMapSlice';
import { convertFeetToMetric, convertMetricToFeet } from '../../util';
import SidePanel from 'components/Basic/SidePanel';
import { useDeleteCustomMap } from 'hooks/api/customMap/useDelCustomMap';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import { useUpdateCustomMap } from 'hooks/api/customMap/useUpdateCustomMap';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { InteractionBlocker } from 'components/InteractionBlocker/interactionBlockerLayer';
import { selectCustomMap, selectHasCustomMapChanges } from 'store/selectors/customMapSelector';
import CustomTooltip from 'components/Basic/Tooltip';
import { pixelsToFeet, getScaleFromFeet } from './util';

export default function ImageEditor() {
	const { uuid, imgHeight, imgWidth, groundMetersPerPixel } = useSelector((rs: RootState) => rs.roofData.data);
	const { selectedMap, isImageEditingEnabled, isCalibrating, originalPixelDistance } = useSelector(selectCustomMap);
	const [activeSlider, setActiveSlider] = useState<string | null>(null);
	const [fieldState, setFieldState] = useState<Record<string, boolean>>({});
	const disptach = useDispatch();

	const [confirm, setConfirm] = useState<boolean>(false);
	const { mutateAsync: deleteCustomMap } = useDeleteCustomMap(uuid);
	const { mutateAsync: updateCustomMap } = useUpdateCustomMap(uuid);

	const [doneBtnLoader, setDoneLoader] = useState<boolean>(false);

	const isFileEditingInProgess = useQueriesRunningInBackGround([
		`update-custom-map-${uuid}`,
	], 'mutating');
	const isBulkUpdatingCustomMaps = useQueriesRunningInBackGround([
		`bulk-update-custom-map-${uuid}`,
	], 'mutating');

	const isUploadingInProgress = useQueriesRunningInBackGround([`upload-custom-map-${uuid}`], 'mutating');
	
	const deleteLoader = useQueriesRunningInBackGround([
		`delete-custom-map-${uuid}`,
	], 'mutating');
	const isModalBlocked = isUploadingInProgress|| isFileEditingInProgess || isBulkUpdatingCustomMaps || deleteLoader;

	useEffect(() => {
		if (!isImageEditingEnabled) {
			setActiveSlider(null);
		}
	}, [isImageEditingEnabled]);

	function handleFileDeleteClick() {
		if (selectedMap) {
			setConfirm(true);
		}
	}

	async function handleFileDeleteConfirmation() {
		if (!selectedMap) return;
		setConfirm(false);
		await deleteCustomMap(selectedMap.id);
		disptach(removeCustomMap(selectedMap.id));
		disptach(toggleImageEditing({ isOpen: false }));
	}

	function toggleFileVisibility() {
		if (!selectedMap) return;
		disptach(toggleMapVisibility({ id: selectedMap.id, isVisible: !selectedMap?.isVisible }));
	}

	function handleSliderChange(
		key: keyof Pick<DesignCustomMap, 'scaleX' | 'scaleY' | 'rotation' | 'opacity'>,
		value: number
	) {
		if (!selectedMap) return;

		const newTransform: Partial<DesignCustomMap> = {};

		if (key === 'scaleX' || key === 'scaleY') {
			if (value == null || value === 0) return;
			const pixelDimension = key === 'scaleX'
				? selectedMap.pixelWidth
				: selectedMap.pixelHeight;

			const newScale = getScaleFromFeet(value, pixelDimension, groundMetersPerPixel);

			// Stretch metersPerPixel by same factor → distance display updates automatically
			const newMetersPerPixel = groundMetersPerPixel * newScale;

			newTransform.scaleX = newScale;
			newTransform.scaleY = newScale;
			newTransform.metersPerPixel = newMetersPerPixel; // ← this drives distance field
		}else {
			if (value == null) return;
			newTransform[key] = value;
		}

		disptach(editMap({ id: selectedMap.id, editedState: newTransform }));
	}

	function handleDistanceChange(distanceInFt: number) {
		if (!originalPixelDistance || !selectedMap) return;

		if (distanceInFt <= 0) return;
		const actualDistanceInMeters = convertFeetToMetric(distanceInFt);

		// New metersPerPixel based purely on the drawn line
		const customMetersPerPxScale = actualDistanceInMeters / originalPixelDistance;
		const correctionFactor = customMetersPerPxScale / selectedMap.metersPerPixel;
		disptach(editMap({
			id: selectedMap.id, editedState: {
				metersPerPixel: customMetersPerPxScale,
				scaleX: selectedMap.scaleX * correctionFactor,
				scaleY: selectedMap.scaleY * correctionFactor
			}
		}));
	}

	async function updateImageTransform() {
		if (!selectedMap) return;
		setDoneLoader(true);
		const updatedMap = await updateCustomMap({
			id: selectedMap.id, payload: {
				scaleX: selectedMap.scaleX,
				scaleY: selectedMap.scaleY,
				opacity: selectedMap.opacity,
				isVisible: selectedMap.isVisible,
				rotation: selectedMap.rotation,
				x: selectedMap.x,
				y: selectedMap.y,
				metersPerPixel: selectedMap.metersPerPixel,
			}
		});
		disptach(setBaselineMap(updatedMap));
		disptach(toggleImageEditing({ isOpen: false }));
		setDoneLoader(false);
	}

	const updateFieldError = (field: string, error: boolean) => {
		setFieldState(prev => {
			if (prev[field] === error) return prev;

			return {
				...prev,
				[field]: error
			};
		});
	};

	const maxHeightFt = convertMetricToFeet(imgHeight * groundMetersPerPixel);
	const maxWidthFt = convertMetricToFeet(imgWidth * groundMetersPerPixel);
	const hasInputError = Object.values(fieldState).some(Boolean);
	const isDirty = useSelector(selectHasCustomMapChanges);

	const disableDoneAction = hasInputError || !isDirty || isModalBlocked;
	return (
		<>
			<SidePanel
				title='Edit Image'
				open={isImageEditingEnabled}
				onAction={updateImageTransform}
				disableAction={disableDoneAction}
				actionLoader={doneBtnLoader}
			>
				{!selectedMap ? (<span>Image Not Found</span>) :
					<InteractionBlocker isBlocking={isModalBlocked}>
						<CustomImageHeading>
							<ImageInfoDiv>
								<ImageNameDiv>
									{/* <label>Name:</label> */}
									<span title={selectedMap.fileName ?? 'Custom'}>{selectedMap.fileName ?? 'Custom'}</span>
								</ImageNameDiv>
							</ImageInfoDiv>
							<ImageActionDiv>
								{selectedMap.isVisible ?
									<>
										<span id="toggle-visibility" onClick={toggleFileVisibility}>
											<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 38 38" fill="none">
												<path d="M36.9336 18.918C36.9336 28.8688 28.8668 36.9355 18.916 36.9355C8.96518 36.9355 0.898438 
											28.8688 0.898438 18.918C0.898438 8.96713 8.96518 0.900391 18.916 0.900391C28.8668 0.900391 36.9336 
											8.96713 36.9336 18.918Z" fill="var(--defaultBgColor)" 
												stroke="var(--controlBorderColor)" strokeWidth="1.80176" strokeLinecap="round" 
												strokeLinejoin="round"/>
												<path d="M18.3188 11.5752C11.3965 12.0849 9.04702 17.7961 8.66118 18.8831C8.62011 18.9988 8.62221 
											19.1216 8.66724 19.2358C9.11662 20.3756 11.9364 26.7055 19.0975 26.5857C26.2128 26.4667 28.7465 
											20.3875 29.1666 19.224C29.2113 19.1003 29.2057 18.9681 29.1512 18.8484C28.6068 17.6529 25.2808 
											11.0626 18.3188 11.5752Z" stroke="var(--defaultIconColor)" strokeWidth="1.63634" strokeLinecap="round"/>
												<path d="M9.75 27.6153L27.2138 10.2207" 
													stroke="var(--defaultIconColor)" strokeWidth="1.63634" strokeLinecap="round"/>
												<path d="M21.6406 15.5622C20.9807 15.1366 20.1951 14.9258 19.2734 14.9258C16.7881 14.9258 14.7734 
												16.9405 14.7734 19.4257C14.7734 20.4387 15.0513 21.3277 15.5831 22.053" 
												stroke="var(--defaultIconColor)" strokeWidth="1.63634"/>
												<path d="M23.0276 16.9805C23.4533 17.6404 23.6641 18.426 23.6641 19.3477C23.6641 21.833 21.6494 
												23.8477 19.1641 23.8477C18.5513 23.8477 17.9837 23.7459 17.4704 23.5478C17.1353 23.4184 16.8233 
												23.248 16.5368 23.038" stroke="var(--defaultIconColor)" strokeWidth="1.63634"/>
											</svg>
										</span>
										<CustomTooltip elementID="toggle-visibility" content="Hide Map" place="bottom" />
									</>
									:
									<>
										<span id="toggle-visibility" onClick={toggleFileVisibility}>	
											<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 38 38" fill="none">
												<path d="M36.9336 18.918C36.9336 28.8688 28.8668 36.9355 18.916 36.9355C8.96518 36.9355 0.898438 
												28.8688 0.898438 18.918C0.898438 8.96713 8.96518 0.900391 18.916 0.900391C28.8668 0.900391 
												36.9336 8.96713 36.9336 18.918Z" fill="var(--defaultBgColor)" stroke="var(--borderColor)" 
												strokeWidth="1.80176" strokeLinecap="round" strokeLinejoin="round"/>
												<path d="M18.2792 10.9131C10.8842 11.4576 8.37431 17.5588 7.96212 18.7199C7.91825 18.8435 7.9205 
											18.9747 7.9686 19.0967C8.44867 20.3144 11.461 27.0765 19.1111 26.9486C26.7122 26.8214 29.419 20.3271 
											29.8678 19.0842C29.9155 18.9521 29.9095 18.8108 29.8513 18.6829C29.2698 17.4058 25.7166 10.3655 18.2792 
											10.9131Z" stroke="var(--defaultIconColor)" strokeWidth="1.65789" strokeLinecap="round"/>
												<circle cx="18.7994" cy="18.8951" r="3.97826" stroke="var(--defaultIconColor)" strokeWidth="1.65789"/>
											</svg>
										</span>
										<CustomTooltip elementID="toggle-visibility" content="Show Map" place="bottom" />
									</>
								}
								<span id="delete-map" onClick={handleFileDeleteClick}>
									{/* <DeleteIcon /> */}
									<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 38 38" fill="none">
										<path d="M36.9336 18.918C36.9336 28.8688 28.8668 36.9355 18.916 36.9355C8.96518 36.9355 0.898438 
										28.8688 0.898438 18.918C0.898438 8.96713 8.96518 0.900391 18.916 0.900391C28.8668 0.900391 36.9336 
										8.96713 36.9336 18.918Z" fill="var(--defaultBgColor)" 
										stroke="var(--borderColor)" strokeWidth="1.80176" strokeLinecap="round" 
										strokeLinejoin="round"/>
										<path d="M12.6041 26.2561L11.7031 12.7422H26.1092L25.2082 26.2561C25.1362 27.3372 24.2382 28.1772 
										23.1548 28.1772H14.6575C13.574 28.1772 12.6761 27.3372 12.6041 26.2561Z" 
										stroke="var(--defaultIconColor)" strokeWidth="1.91"/>
										<path d="M9.64844 12.7422H28.1705" stroke="var(--defaultIconColor)" strokeWidth="1.91" strokeLinecap="round"/>
										<path d="M15.6539 10.2191L15.1737 11.6597C14.996 12.1928 15.3927 12.7433 15.9546 
										12.7433H21.9024C22.4643 12.7433 22.861 12.1928 22.6833 11.6597L22.2031 10.2191C22.0911 
										9.88298 21.7765 9.65625 21.4222 9.65625H16.4348C16.0805 9.65625 15.7659 9.88298 
										15.6539 10.2191Z" stroke="var(--defaultIconColor)" strokeWidth="1.91" strokeLinecap="round"/>
										<path d="M16.8594 17.8887V23.0337" stroke="var(--defaultIconColor)" strokeWidth="1.91" strokeLinecap="round"/>
										<path d="M20.7578 17.0645V22.2095" stroke="var(--defaultIconColor)" strokeWidth="1.91" strokeLinecap="round"/>
									</svg>
								</span>
								<CustomTooltip elementID="delete-map" content="Delete Map" place="bottom" />
							</ImageActionDiv>
						</CustomImageHeading>
						<ImageinfoWrap>
							<ImageEditorDiv>
								{/* <ImageInfoDiv>
									<ImageNameDiv>
										<label>Name:</label>
										<span>{selectedMap.fileName}</span>
									</ImageNameDiv>
								</ImageInfoDiv> */}
								<ImageEditornputField>
									<SliderInputField
										label='Height'
										id='height'
										min={0}
										max={maxHeightFt}
										value={pixelsToFeet(selectedMap.pixelHeight, 1, selectedMap.metersPerPixel)}
										onInputChange={(val) => handleSliderChange('scaleY', val)}
										isActive={activeSlider === 'height'}
										onActivate={() => setActiveSlider('height')}
										unit='ft'
										showError={false}

									/>
									<SliderInputField
										label='Width'
										id='width'
										min={0}
										max={maxWidthFt}
										value={pixelsToFeet(selectedMap.pixelWidth, 1, selectedMap.metersPerPixel)}
										onInputChange={(val) => handleSliderChange('scaleX', val)}
										isActive={activeSlider === 'width'}
										onActivate={() => setActiveSlider('width')}
										unit='ft'
										showError={false}

									/>
									<SliderInputField
										label='Rotation'
										id='rotation'
										min={0}
										max={360}
										value={selectedMap.rotation}
										onInputChange={(val) => handleSliderChange('rotation', val)}
										isActive={activeSlider === 'rotation'}
										onActivate={() => setActiveSlider('rotation')}
										unit='deg'
										onErrorChange={(err) => updateFieldError('rotation', err)}

									/>
									<SliderInputField
										label='Opacity'
										id='opacity'
										min={0}
										max={100}
										value={selectedMap.opacity}
										onInputChange={(val) => handleSliderChange('opacity', val)}
										isActive={activeSlider === 'opacity'}
										onActivate={() => setActiveSlider('opacity')}
										unit='%'
										onErrorChange={(err) => updateFieldError('opacity', err)}

									/>

									{originalPixelDistance != null && (
										<>
											<SliderInputField
												id='actual'
												label='Actual Distance'
												min={1}
												max={convertMetricToFeet(selectedMap.pixelWidth * groundMetersPerPixel)}
												value={pixelsToFeet(originalPixelDistance, 1, selectedMap.metersPerPixel)}
												onInputChange={(val) => handleDistanceChange(val)}
												unit='ft'
												disabled={!isCalibrating}
												isActive={activeSlider === 'distance'}
												onActivate={() => setActiveSlider('distance')}
												showError={false}
											/>
										</>)}

								</ImageEditornputField>
							</ImageEditorDiv>
						</ImageinfoWrap>
					</InteractionBlocker>
				}

			</SidePanel>

			<ConfirmationModal
				open={confirm}
				message={`Are you sure you want to delete the ${selectedMap?.fileName ?? ''} image?`}
				onCancel={() => setConfirm(false)}
				onConfirm={handleFileDeleteConfirmation}
			/>

		</>
	);
}
